import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  DocumentTextIcon, 
  MagnifyingGlassIcon,
  ArrowRightIcon,
  FolderIcon,
  ArrowTopRightOnSquareIcon,
  GlobeAltIcon
} from '@heroicons/react/24/outline'
import { ESTRUCTURA_LEGAL } from '../../data/normatividad'
import normatividadService from '../../services/normatividadService'
import { CategoriaNavegacion, ItemNavegacion, TemaNormativo } from '../../types/normatividad'

interface SeccionNormatividadProps {
  basePath?: string
}

function contarItemsNavegacion(items: ItemNavegacion[]): number {
  return items.reduce((total, item) => {
    const esDocumento = item.tipo === 'DOC' || item.tipo === 'LINK'
    return total + (esDocumento ? 1 : 0) + (item.subItems ? contarItemsNavegacion(item.subItems) : 0)
  }, 0)
}

const SeccionNormatividad: React.FC<SeccionNormatividadProps> = ({ 
  basePath = '/institucion/normas' 
}) => {
  const navigate = useNavigate()
  
  // Estado para datos dinámicos
  const [todasLasNormas, setTodasLasNormas] = useState<TemaNormativo[]>([])
  const [estructuraLegal, setEstructuraLegal] = useState<CategoriaNavegacion[]>(ESTRUCTURA_LEGAL)

  // Estado para navegación
  const [tabActiva, setTabActiva] = useState<string>('leyes')
  const [busqueda, setBusqueda] = useState('')

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        const normas = await normatividadService.getAllNormas()
        setTodasLasNormas(normas)
        const estructura = await normatividadService.getEstructuraNavegacion()
        setEstructuraLegal(estructura)
      } catch (error) {
        console.error("Error cargando normatividad", error)
      }
    }
    cargarDatos()

    // Escuchar cambios en la "base de datos" local
    const handleUpdate = () => cargarDatos()
    window.addEventListener('normatividad_updated', handleUpdate)
    
    return () => window.removeEventListener('normatividad_updated', handleUpdate)
  }, [])

  const manejarClickItem = (item: ItemNavegacion) => {
    if (item.tipo === 'DOC' && item.id_doc) {
      navigate(`${basePath}/${item.id_doc}`)
    } else if (item.tipo === 'LINK' && item.url) {
      window.open(item.url, '_blank')
    }
  }

  const manejarClickResultado = (resultado: TemaNormativo) => {
    navigate(`${basePath}/${resultado.id}`)
  }

  // Filtrado para el buscador global
  const resultadosBusqueda = busqueda 
    ? todasLasNormas.filter(doc => 
        doc.titulo.toLowerCase().includes(busqueda.toLowerCase()) ||
        doc.descripcion.toLowerCase().includes(busqueda.toLowerCase()) ||
        (doc.contenido && doc.contenido.toLowerCase().includes(busqueda.toLowerCase()))
      )
    : []

  const categoriaActiva = estructuraLegal.find(c => c.id === tabActiva)
  const totalDocumentos = todasLasNormas.length
  const totalCategorias = estructuraLegal.length
  const documentosCategoriaActiva = categoriaActiva
    ? categoriaActiva.grupos.reduce((total, grupo) => total + contarItemsNavegacion(grupo.items), 0)
    : 0

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-[34px] border border-slate-200/60 bg-white/70 shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] backdrop-blur-md dark:border-zinc-800/50 dark:bg-[#000000] dark:bg-none">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(16,185,129,0.18),_transparent_20%),radial-gradient(circle_at_top_right,_rgba(96,165,250,0.12),_transparent_18%),linear-gradient(180deg,rgba(255,255,255,0.24),rgba(255,255,255,0.06))] dark:bg-none" />
        
        <div className="relative border-b border-slate-200/60 px-5 py-6 dark:border-zinc-800/50 md:px-6">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/90 px-3.5 py-2 text-xs font-medium text-emerald-700 shadow-sm transition-transform duration-300 hover:-translate-y-0.5 dark:border-zinc-800 dark:bg-zinc-900/50 dark:text-emerald-400">
                <GlobeAltIcon className="h-4 w-4" />
                Biblioteca legal
              </div>
              <div>
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-zinc-100">
                  Base normativa
                </h2>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 dark:text-zinc-400">
                  Encuentra leyes, tratados y criterios con una experiencia más clara para estudiar, consultar y relacionar conceptos de comercio exterior.
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm transition-colors duration-200 hover:border-slate-300 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/50">
                  <p className="text-[11px] font-medium text-slate-500 dark:text-zinc-500">
                    Documentos
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-zinc-100">
                    {totalDocumentos}
                  </p>
                </div>
                <div className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm transition-colors duration-200 hover:border-slate-300 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/50">
                  <p className="text-[11px] font-medium text-slate-500 dark:text-zinc-500">
                    Categorías
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-zinc-100">
                    {totalCategorias}
                  </p>
                </div>
                <div className="rounded-[24px] border border-slate-200/80 bg-white p-4 shadow-sm transition-colors duration-200 hover:border-slate-300 hover:shadow-md dark:border-zinc-800 dark:bg-zinc-900/50">
                  <p className="text-[11px] font-medium text-slate-500 dark:text-zinc-500">
                    En esta vista
                  </p>
                  <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-zinc-100">
                    {documentosCategoriaActiva}
                  </p>
                </div>
              </div>
            </div>

            <div className="relative w-full xl:max-w-md">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
                <MagnifyingGlassIcon className="h-5 w-5 text-slate-400 dark:text-zinc-500" aria-hidden="true" />
              </div>
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="block w-full rounded-full border border-white bg-white py-3.5 pl-11 pr-4 text-sm font-medium text-slate-900 shadow-[0_12px_22px_-18px_rgba(15,23,42,0.18)] outline-none transition-colors duration-200 placeholder:font-normal placeholder:text-slate-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/10 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100 dark:placeholder:text-zinc-500 dark:focus:border-blue-500 dark:focus:ring-blue-500/20"
                placeholder="Busca leyes, tratados, fracciones o conceptos..."
                aria-label="Buscar en la base normativa"
              />
            </div>
          </div>
        </div>
      </div>

      {busqueda ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-500">
              Coincidencias ({resultadosBusqueda.length})
            </h3>
            <button
              type="button"
              onClick={() => setBusqueda('')}
              className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-slate-600 transition hover:bg-slate-50 hover:text-slate-900 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700 dark:hover:text-white"
            >
              Restablecer Consulta
            </button>
          </div>
          {resultadosBusqueda.length > 0 ? (
            <div className="grid grid-cols-1 gap-3">
              {resultadosBusqueda.map((res) => (
                <div
                  key={res.id}
                  onClick={() => manejarClickResultado(res)}
                  className="group cursor-pointer rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition duration-200 hover:border-sky-400 dark:border-zinc-800 dark:bg-zinc-900/50 dark:hover:border-zinc-700"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="rounded-sm bg-sky-500/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.2em] text-sky-700 dark:bg-zinc-800 dark:text-zinc-300">
                          {res.tipo}
                        </span>
                        <span className="text-[10px] font-bold uppercase tracking-wide text-slate-400 dark:text-zinc-500">
                          Rev: {res.actualizado}
                        </span>
                      </div>
                      <h4 className="mt-3 text-sm font-bold text-slate-900 transition group-hover:text-sky-600 dark:text-zinc-100 dark:group-hover:text-white">
                        {res.titulo}
                      </h4>
                      <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-zinc-400">
                        {res.descripcion}
                      </p>
                    </div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-2.5 text-slate-400 transition group-hover:border-sky-300 group-hover:text-sky-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-500 dark:group-hover:border-zinc-600 dark:group-hover:text-zinc-300">
                      <ArrowRightIcon className="h-4 w-4" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-[24px] border border-dashed border-slate-300 bg-white px-6 py-14 text-center dark:border-zinc-800 dark:bg-zinc-900/30">
              <DocumentTextIcon className="mx-auto mb-4 h-12 w-12 text-slate-300 dark:text-zinc-700" />
              <p className="text-xs font-bold uppercase tracking-wide text-slate-700 dark:text-zinc-400">
                SIN RESULTADOS EN LA BASE
              </p>
              <p className="mt-2 text-xs text-slate-500 dark:text-zinc-600">
                Verifica los términos de la consulta normativa.
              </p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="overflow-x-auto pb-2">
            <nav className="flex min-w-max gap-2" aria-label="Tabs">
              {estructuraLegal.map((cat) => {
                const esActiva = tabActiva === cat.id
                const Icono = cat.icono || FolderIcon
                return (
                  <button
                    key={cat.id}
                    onClick={() => setTabActiva(cat.id)}
                    className={`
                      group inline-flex items-center gap-2 rounded-full border px-4 py-2.5 text-xs font-bold transition-all duration-200
                      ${esActiva
                        ? 'border-sky-200 bg-sky-50 text-sky-700 shadow-sm dark:border-zinc-700 dark:bg-zinc-800 dark:bg-none dark:text-zinc-100'
                        : 'border-slate-200/60 bg-white text-slate-500 hover:bg-slate-50 hover:border-slate-300 dark:border-zinc-800/50 dark:bg-zinc-900/50 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-200'
                      }
                    `}
                  >
                    {Icono ? (
                      <Icono className={`
                        h-4 w-4
                        ${esActiva ? 'text-sky-600 dark:text-zinc-300' : 'text-slate-400 group-hover:text-slate-500 dark:text-zinc-500'}
                      `} aria-hidden="true" />
                    ) : null}
                    {cat.titulo}
                  </button>
                )
              })}
            </nav>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
            {categoriaActiva?.grupos.map((grupo, idx) => (
                <div 
                  key={idx} 
                  className="flex flex-col overflow-hidden rounded-[30px] border border-slate-200/60 bg-white shadow-[12px_0_32px_-16px_rgba(0,0,0,0.05)] transition-all duration-200 hover:border-slate-300 hover:shadow-lg dark:border-zinc-800/80 dark:bg-[#000000] dark:hover:bg-zinc-900/20"
                >
                  <div className="border-b border-slate-200/60 bg-slate-50/50 px-5 py-4 dark:border-zinc-800/50 dark:bg-zinc-900/30">
                    <div className="flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-slate-900 dark:text-zinc-100">
                        {grupo.titulo}
                      </h3>
                      <span className="rounded-full bg-slate-900/8 px-2.5 py-1 text-[10px] font-medium text-slate-600 dark:bg-zinc-800 dark:text-zinc-400">
                        {grupo.items.length} DOCS
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex-1 p-3">
                    <ul className="space-y-2">
                      {grupo.items.map((item, i) => (
                        <li key={i} className="space-y-1">
                          <div 
                            onClick={() => item.tipo !== 'HISTORICO' && item.tipo !== 'GRUPO' && manejarClickItem(item)}
                            className={`
                              group/item flex items-center justify-between rounded-2xl border px-3 py-2.5 transition-all
                              ${(item.tipo === 'HISTORICO' || item.tipo === 'GRUPO') 
                                ? 'cursor-default border-transparent bg-transparent' 
                                : 'cursor-pointer border-slate-200/60 bg-white shadow-sm hover:border-sky-300 hover:shadow-md dark:border-zinc-800/50 dark:bg-zinc-900/50 dark:hover:border-zinc-700 dark:hover:bg-zinc-800'
                              }
                            `}
                          >
                            <div className="flex min-w-0 items-start gap-3">
                              <div className={`
                                mt-0.5 rounded-lg p-1.5
                                ${(item.tipo === 'HISTORICO' || item.tipo === 'GRUPO')
                                  ? 'bg-slate-100 text-slate-400 dark:bg-zinc-800/50 dark:text-zinc-500'
                                  : 'bg-sky-50 text-sky-600 dark:bg-zinc-800 dark:text-zinc-300'
                                }
                              `}>
                                {item.tipo === 'LINK' ? (
                                  <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                                ) : (item.tipo === 'HISTORICO' || item.tipo === 'GRUPO') ? (
                                  <FolderIcon className="h-3.5 w-3.5" />
                                ) : (
                                  <DocumentTextIcon className="h-3.5 w-3.5" />
                                )}
                              </div>
                              <div className="min-w-0 py-0.5">
                                <span className={`block text-xs leading-5 ${
                                  (item.tipo === 'HISTORICO' || item.tipo === 'GRUPO')
                                    ? 'font-bold uppercase tracking-wide text-slate-500 dark:text-zinc-500' 
                                    : 'font-medium text-slate-700 group-hover/item:text-sky-700 dark:text-zinc-300 dark:group-hover/item:text-white'
                                }`}>
                                  {item.titulo}
                                </span>
                              </div>
                            </div>
                            {item.etiqueta && (
                              <span className="ml-3 shrink-0 rounded-sm border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400">
                                {item.etiqueta}
                              </span>
                            )}
                          </div>

                          {item.subItems && item.subItems.length > 0 && (
                            <ul className="ml-4 space-y-1 border-l border-slate-200 pl-4 dark:border-zinc-800">
                              {item.subItems.map((sub, j) => (
                                <li 
                                  key={j}
                                  onClick={() => manejarClickItem(sub)}
                                  className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[11px] font-medium text-slate-500 transition hover:bg-sky-50 hover:text-sky-700 dark:text-zinc-500 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
                                >
                                  <div className="h-1 w-1 rounded-full bg-slate-300 dark:bg-zinc-600" />
                                  <span>{sub.titulo}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
        </div>
      )}
    </div>
  )
}

export default SeccionNormatividad
